import { ArrowRight, Star, ShieldCheck, Users, Lock } from "lucide-react";
import { Link } from "wouter";
import { useTranslation } from "@/i18n/useTranslation";
import { Reveal } from "@/components/Reveal";

export function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-12 md:pt-20 pb-16 md:pb-24 overflow-hidden">
      {/* soft brand glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 right-[-10%] w-[520px] h-[520px] rounded-full opacity-[0.18] blur-3xl"
        style={{ background: "radial-gradient(circle, hsl(208 71% 38%) 0%, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-32 left-[-8%] w-[420px] h-[420px] rounded-full opacity-[0.12] blur-3xl"
        style={{ background: "radial-gradient(circle, hsl(208 71% 38%) 0%, transparent 70%)" }}
      />

      <div className="container mx-auto max-w-[1440px] px-5 md:px-6">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-6 text-center lg:text-left">
            <Reveal initial delay={0} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3.5 py-1.5 text-xs font-medium text-foreground/80 shadow-sm">
              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
              {t("hero.badge")}
            </Reveal>

            <h1 className="mt-6 text-[2rem] sm:text-[2.4rem] md:text-[2.85rem] font-bold tracking-tight leading-[1.12] text-foreground">
              {t("hero.title.before")}
              <span className="relative inline-block text-primary">
                {t("hero.title.accent")}
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-1 h-[0.35em] -z-10 bg-primary/15 rounded-sm"
                />
              </span>
              {t("hero.title.after")}
            </h1>

            <p className="mt-5 max-w-xl mx-auto lg:mx-0 text-sm md:text-[0.95rem] text-muted-foreground leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center lg:items-start lg:justify-start justify-center gap-3">
              <Link
                href="/shop"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground border border-[hsl(208,71%,30%)] hover:bg-[hsl(208,71%,34%)] transition-colors"
              >
                {t("nav.signupPro")} <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://admin.cookierobot.info/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-semibold text-foreground border border-border hover:bg-muted transition-colors"
              >
                {t("nav.signupFree")}
              </a>
            </div>

            <div className="mt-5 inline-flex items-center justify-center lg:justify-start gap-2 text-xs text-muted-foreground">
              <Users className="w-3.5 h-3.5 text-primary" />
              <span>{t("hero.trust.users")}</span>
            </div>

            <div className="mt-7 flex items-center justify-center lg:justify-start gap-5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-primary" /> DSGVO
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-primary" /> ePrivacy
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-primary" /> TTDSG
              </span>
            </div>
          </div>

          {/* Right: floating mockup */}
          <div className="lg:col-span-6 relative">
            <BrowserMockup />

            {/* floating brand card */}
            <div className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 rounded-xl border border-border bg-white shadow-lg pl-3 pr-4 py-2.5">
              <img
                src="/cookierobot-logo.webp"
                alt=""
                width={120}
                height={40}
                loading="lazy"
                decoding="async"
                className="h-10 w-auto object-contain"
              />
              <div className="text-xs leading-tight">
                <div className="font-semibold text-foreground">{t("mockup.users")}</div>
                <div className="text-muted-foreground">{t("mockup.usersSub")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrowserMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative rounded-2xl border border-border bg-white shadow-[0_30px_60px_-30px_rgba(15,23,42,0.22)] overflow-hidden">
      {/* Browser chrome */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-[#FBFBFC]">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="px-3 py-1 rounded-md bg-white border border-border text-[11px] text-muted-foreground max-w-md w-full text-center truncate">
            {t("mockup.url")}
          </div>
        </div>
        <div className="w-10" />
      </div>

      {/* Page body */}
      <div className="relative h-[400px] md:h-[440px] bg-gradient-to-b from-[#F8F9FB] to-white p-6 md:p-8">
        {/* Faded mock website */}
        <div className="opacity-50 space-y-4">
          <div className="h-2.5 w-24 rounded bg-slate-200" />
          <div className="h-5 w-3/4 rounded bg-slate-300" />
          <div className="h-5 w-1/2 rounded bg-slate-300" />
          <div className="space-y-2 mt-5">
            <div className="h-2 w-full rounded bg-slate-200" />
            <div className="h-2 w-11/12 rounded bg-slate-200" />
            <div className="h-2 w-9/12 rounded bg-slate-200" />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            {[0, 1].map((i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                <div className="w-7 h-7 rounded bg-slate-200" />
                <div className="h-2 w-2/3 rounded bg-slate-200" />
                <div className="h-1.5 w-full rounded bg-slate-100" />
              </div>
            ))}
          </div>
        </div>

        {/* Floating brand button */}
        <div className="absolute bottom-4 left-4 w-11 h-11 rounded-full bg-white flex items-center justify-center shadow-lg ring-4 ring-primary/15 border border-border">
          <img src="/cookierobot-logo.webp" alt="" width={28} height={28} loading="lazy" decoding="async" className="w-7 h-7 object-contain" />
        </div>

        {/* Consent banner card */}
        <div className="absolute bottom-4 right-4 w-[260px] rounded-xl border border-border bg-white shadow-[0_20px_40px_-12px_rgba(15,23,42,0.18)] overflow-hidden">
          <div className="px-3.5 pt-3 pb-2 border-b border-border/70">
            <div className="text-[12px] font-semibold text-primary">{t("mockup.title")}</div>
          </div>
          <div className="p-3.5 space-y-2">
            <ConsentRow label={t("mockup.necessary")} on locked />
            <ConsentRow label={t("mockup.preferences")} on />
            <ConsentRow label={t("mockup.statistics")} />
            <ConsentRow label={t("mockup.marketing")} />
          </div>
          <div className="p-2.5 pt-1 grid grid-cols-2 gap-1.5">
            <button className="rounded-md border border-border bg-white px-2 py-1.5 text-[10px] font-semibold text-foreground">
              {t("mockup.savePref")}
            </button>
            <button className="rounded-md bg-primary px-2 py-1.5 text-[10px] font-semibold text-primary-foreground border border-[hsl(208,71%,30%)]">
              {t("mockup.acceptAll")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsentRow({ label, on, locked }: { label: string; on?: boolean; locked?: boolean }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-foreground/85 inline-flex items-center gap-1.5">
        {locked && <Lock className="w-2.5 h-2.5 text-muted-foreground" />}
        {label}
      </span>
      <span
        className={`relative inline-block w-7 h-[16px] rounded-full transition-colors ${
          on ? "bg-primary" : "bg-slate-200"
        }`}
      >
        <span
          className={`absolute top-0.5 ${on ? "right-0.5" : "left-0.5"} w-3 h-3 rounded-full bg-white shadow`}
        />
      </span>
    </div>
  );
}
